import React, { useState, useEffect } from 'react';
import { 
  Car, 
  Plus, 
  Trash2, 
  Edit, 
  Calendar, 
  MapPin, 
  DollarSign, 
  Save, 
  X, 
  Search, 
  Filter, 
  AlertCircle,
  Plane,
  Train,
  Bus,
  Ship,
  Clock
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { SearchInput } from '../common/SearchInput';

interface Transportation {
  id: string;
  trip_id: string;
  type: 'flight' | 'train' | 'bus' | 'car' | 'ferry' | 'other';
  provider: string;
  departure_location: string;
  arrival_location: string;
  departure_time: string;
  arrival_time: string;
  confirmation_number?: string;
  total_cost?: number;
  currency?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface Trip {
  id: string;
  title: string;
  destination?: string;
}

export function Transportation() {
  const { user } = useAuth();
  const [transportation, setTransportation] = useState<Transportation[]>([]);
  const [trips, setTrips] = useState<Trip[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [tripFilter, setTripFilter] = useState<string>('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedTransportation, setSelectedTransportation] = useState<Transportation | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  
  const [formData, setFormData] = useState({
    trip_id: '',
    type: 'flight' as const,
    provider: '',
    departure_location: '',
    arrival_location: '',
    departure_time: '',
    arrival_time: '',
    confirmation_number: '',
    total_cost: '',
    currency: 'AUD',
    notes: ''
  });

  const typeOptions = [
    { value: 'flight', label: 'Flight', icon: <Plane size={16} /> },
    { value: 'train', label: 'Train', icon: <Train size={16} /> },
    { value: 'bus', label: 'Bus', icon: <Bus size={16} /> },
    { value: 'car', label: 'Car', icon: <Car size={16} /> },
    { value: 'ferry', label: 'Ferry', icon: <Ship size={16} /> },
    { value: 'other', label: 'Other', icon: <Car size={16} /> }
  ];

  const currencyOptions = ['AUD', 'USD', 'EUR', 'GBP', 'CAD', 'JPY'];

  useEffect(() => {
    const loadData = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        
        // Load trips first
        const { data: tripsData, error: tripsError } = await supabase
          .from('trips')
          .select('id, title, destination')
          .eq('user_id', user.id)
          .order('start_date', { ascending: true });
          
        if (tripsError) throw tripsError;
        setTrips(tripsData || []);

        // Load transportation - all for user
        const { data: transportationData, error: transportationError } = await supabase
          .from('transportation')
          .select('*')
          .eq('user_id', user.id)
          .order('departure_date', { ascending: true });
          
        if (transportationError) throw transportationError;
        setTransportation(transportationData || []);
      } catch (err) {
        console.error('Error loading data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [user]);

  const handleAddTransportation = () => {
    setFormData({
      trip_id: '',
      type: 'flight',
      provider: '',
      departure_location: '',
      arrival_location: '',
      departure_time: '',
      arrival_time: '',
      confirmation_number: '',
      total_cost: '',
      currency: 'AUD',
      notes: ''
    });
    setIsEditing(false);
    setShowAddModal(true);
  };

  const handleEditTransportation = (transport: Transportation) => {
    setSelectedTransportation(transport);
    setFormData({
      trip_id: transport.trip_id,
      type: transport.type,
      provider: transport.provider,
      departure_location: transport.departure_location,
      arrival_location: transport.arrival_location,
      departure_time: transport.departure_time,
      arrival_time: transport.arrival_time,
      confirmation_number: transport.confirmation_number || '',
      total_cost: transport.total_cost?.toString() || '',
      currency: transport.currency || 'AUD',
      notes: transport.notes || ''
    });
    setIsEditing(true);
    setShowAddModal(true);
  };

  const handleViewTransportation = (transport: Transportation) => {
    setSelectedTransportation(transport);
    setShowViewModal(true);
  };

  const handleDeleteTransportation = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this transportation? This action cannot be undone.')) {
      try {
        const { error } = await supabase
          .from('transportation')
          .delete()
          .eq('id', id);
          
        if (error) throw error;
        
        setTransportation(transportation.filter(t => t.id !== id));
      } catch (err) {
        console.error('Error deleting transportation:', err);
        setError(err instanceof Error ? err.message : 'Failed to delete transportation');
      }
    }
  };

  const handleSaveTransportation = async () => {
    // Validate form
    if (!formData.trip_id || !formData.provider || !formData.departure_location || 
        !formData.arrival_location || !formData.departure_time || !formData.arrival_time) {
      setError('Please fill in all required fields');
      return;
    }

    if (new Date(formData.departure_time) > new Date(formData.arrival_time)) {
      setError('Arrival time must be after departure time');
      return;
    }

    try {
      // Parse datetime strings to separate date and time
      const departureDateTime = formData.departure_time ? new Date(formData.departure_time) : null;
      const arrivalDateTime = formData.arrival_time ? new Date(formData.arrival_time) : null;
      
      const transportationData = {
        trip_id: formData.trip_id || null,
        user_id: user.id,
        type: formData.type,
        provider: formData.provider,
        departure_location: formData.departure_location,
        arrival_location: formData.arrival_location,
        departure_date: departureDateTime ? departureDateTime.toISOString().split('T')[0] : null,
        departure_time: departureDateTime ? departureDateTime.toTimeString().split(' ')[0].substring(0, 5) : null,
        arrival_date: arrivalDateTime ? arrivalDateTime.toISOString().split('T')[0] : null,
        arrival_time: arrivalDateTime ? arrivalDateTime.toTimeString().split(' ')[0].substring(0, 5) : null,
        confirmation_number: formData.confirmation_number || null,
        price: formData.total_cost ? parseFloat(formData.total_cost) : null,
        currency: formData.currency,
        notes: formData.notes || null
      };

      if (isEditing && selectedTransportation) {
        const { data, error } = await supabase
          .from('transportation')
          .update(transportationData)
          .eq('id', selectedTransportation.id)
          .select()
          .single();
          
        if (error) throw error;
        
        setTransportation(transportation.map(t => t.id === selectedTransportation.id ? data : t));
      } else {
        const { data, error } = await supabase
          .from('transportation')
          .insert(transportationData)
          .select()
          .single();
          
        if (error) throw error;
        
        setTransportation([...transportation, data]);
      }
      
      setShowAddModal(false);
      setError(null);
    } catch (err) {
      console.error('Error saving transportation:', err);
      setError(err instanceof Error ? err.message : 'Failed to save transportation');
    }
  };

  const getTypeIcon = (type: string) => {
    return typeOptions.find(t => t.value === type)?.icon || <Car size={16} />;
  };

  const formatDateTime = (dateTimeString: string) => {
    return new Date(dateTimeString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const getTripTitle = (tripId: string) => {
    return trips.find(t => t.id === tripId)?.title || 'Unknown Trip';
  };

  const filteredTransportation = transportation.filter(transport => {
    const matchesSearch = transport.provider.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         transport.departure_location.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         transport.arrival_location.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = typeFilter === 'all' || transport.type === typeFilter;
    const matchesTrip = tripFilter === 'all' || transport.trip_id === tripFilter;
    return matchesSearch && matchesType && matchesTrip;
  });

  if (loading) {
    return (
      <div className="space-y-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Transportation</h1>
            <p className="text-gray-600 mt-1">Manage your flights, trains, and other transport</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <div className="p-6">
                <div className="h-4 bg-gray-200 rounded mb-4"></div>
                <div className="h-3 bg-gray-200 rounded mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-2/3"></div>
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Transportation</h1>
          <p className="text-gray-600 mt-1">Manage your flights, trains, and other transport</p>
        </div>
        <Button
          variant="primary"
          onClick={handleAddTransportation}
          icon={<Plus size={20} />}
        >
          Add Transportation
        </Button>
      </div>

      {/* Search and Filters */}
      <div className="flex gap-4">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search transportation..."
          className="flex-1"
        />
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Types</option>
          {typeOptions.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <select
          value={tripFilter}
          onChange={(e) => setTripFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="all">All Trips</option>
          {trips.map(trip => (
            <option key={trip.id} value={trip.id}>{trip.title}</option>
          ))}
        </select>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
          <AlertCircle className="text-red-500" size={20} />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {/* Transportation Grid */}
      {filteredTransportation.length === 0 ? (
        <Card className="text-center py-12">
          <Plane className="mx-auto text-gray-400 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No transportation found</h3>
          <p className="text-gray-600 mb-4">
            {searchQuery || typeFilter !== 'all' || tripFilter !== 'all'
              ? 'Try adjusting your search or filters'
              : 'Start by adding your first transportation booking'
            }
          </p>
          {!searchQuery && typeFilter === 'all' && tripFilter === 'all' && (
            <Button
              variant="primary"
              onClick={handleAddTransportation}
              icon={<Plus size={20} />}
            >
              Add Your First Transportation
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredTransportation.map((transport) => (
            <Card key={transport.id} className="hover:shadow-lg transition-shadow">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{transport.provider}</h3>
                    <div className="flex items-center gap-1 text-sm text-blue-600 mb-2">
                      {getTypeIcon(transport.type)}
                      <span className="capitalize">{transport.type}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Trip:</span> {getTripTitle(transport.trip_id)}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <MapPin size={14} />
                    <span>{transport.departure_location} → {transport.arrival_location}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Clock size={14} />
                    <span>{formatDateTime(transport.departure_time)} - {formatDateTime(transport.arrival_time)}</span>
                  </div>
                  {transport.total_cost && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <DollarSign size={14} />
                      <span>{formatCurrency(transport.total_cost, transport.currency || 'AUD')}</span>
                    </div>
                  )}
                </div>

                {transport.notes && (
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">{transport.notes}</p>
                )}

                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    Created {formatDate(transport.created_at)}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewTransportation(transport)}
                      icon={<MapPin size={14} />}
                    >
                      View
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditTransportation(transport)}
                      icon={<Edit size={14} />}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteTransportation(transport.id)}
                      icon={<Trash2 size={14} />}
                      className="text-red-600 hover:text-red-700"
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title={isEditing ? 'Edit Transportation' : 'Add New Transportation'}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Trip *</label>
            <select
              value={formData.trip_id}
              onChange={(e) => setFormData({ ...formData, trip_id: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select a trip</option>
              {trips.map(trip => (
                <option key={trip.id} value={trip.id}>{trip.title}</option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as any })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {typeOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
              <select
                value={formData.currency}
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {currencyOptions.map(currency => (
                  <option key={currency} value={currency}>{currency}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Provider *</label>
            <input
              type="text"
              value={formData.provider}
              onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Airline, train company, etc."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Departure Location *</label>
              <input
                type="text"
                value={formData.departure_location}
                onChange={(e) => setFormData({ ...formData, departure_location: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Airport, station, etc."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Arrival Location *</label>
              <input
                type="text"
                value={formData.arrival_location}
                onChange={(e) => setFormData({ ...formData, arrival_location: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Airport, station, etc."
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Departure Time *</label>
              <input
                type="datetime-local"
                value={formData.departure_time}
                onChange={(e) => setFormData({ ...formData, departure_time: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Arrival Time *</label>
              <input
                type="datetime-local"
                value={formData.arrival_time}
                onChange={(e) => setFormData({ ...formData, arrival_time: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Confirmation Number</label>
            <input
              type="text"
              value={formData.confirmation_number}
              onChange={(e) => setFormData({ ...formData, confirmation_number: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Booking confirmation number"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Total Cost</label>
            <input
              type="number"
              value={formData.total_cost}
              onChange={(e) => setFormData({ ...formData, total_cost: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="0.00"
              step="0.01"
              min="0"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder="Additional notes..."
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <Button
            variant="outline"
            onClick={() => setShowAddModal(false)}
            icon={<X size={16} />}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveTransportation}
            icon={<Save size={16} />}
          >
            {isEditing ? 'Update Transportation' : 'Create Transportation'}
          </Button>
        </div>
      </Modal>

      {/* View Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title={selectedTransportation?.provider || 'Transportation Details'}
      >
        {selectedTransportation && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Trip</label>
                <p className="text-gray-900">{getTripTitle(selectedTransportation.trip_id)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <div className="flex items-center gap-1 text-blue-600">
                  {getTypeIcon(selectedTransportation.type)}
                  <span className="capitalize">{selectedTransportation.type}</span>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
              <p className="text-gray-900">{selectedTransportation.provider}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Departure Location</label>
                <p className="text-gray-900">{selectedTransportation.departure_location}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Arrival Location</label>
                <p className="text-gray-900">{selectedTransportation.arrival_location}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Departure Time</label>
                <p className="text-gray-900">{formatDateTime(selectedTransportation.departure_time)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Arrival Time</label>
                <p className="text-gray-900">{formatDateTime(selectedTransportation.arrival_time)}</p>
              </div>
            </div>

            {selectedTransportation.confirmation_number && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirmation Number</label>
                <p className="text-gray-900">{selectedTransportation.confirmation_number}</p>
              </div>
            )}

            {selectedTransportation.total_cost && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Cost</label>
                <p className="text-gray-900">{formatCurrency(selectedTransportation.total_cost, selectedTransportation.currency || 'AUD')}</p>
              </div>
            )}

            {selectedTransportation.notes && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                <p className="text-gray-900">{selectedTransportation.notes}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Created</label>
                <p className="text-gray-900">{formatDate(selectedTransportation.created_at)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last Updated</label>
                <p className="text-gray-900">{formatDate(selectedTransportation.updated_at)}</p>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 mt-6">
          <Button
            variant="outline"
            onClick={() => setShowViewModal(false)}
          >
            Close
          </Button>
          {selectedTransportation && (
            <Button
              variant="primary"
              onClick={() => {
                setShowViewModal(false);
                handleEditTransportation(selectedTransportation);
              }}
              icon={<Edit size={16} />}
            >
              Edit Transportation
            </Button>
          )}
        </div>
      </Modal>
    </div>
  );
} 